"use client"

import { useEffect, useMemo, useState } from "react"
import Image from "next/image"
import Link from "next/link"

import type { SliderHeroSlide } from "@lib/data/design"

type SliderHeroProps = {
  slides: SliderHeroSlide[]
}

const AUTOPLAY_INTERVAL_MS = 6000

const normalizeSlides = (slides: SliderHeroSlide[]) =>
  slides.filter((slide) => typeof slide.imageUrl === "string" && slide.imageUrl.trim().length > 0)

const SliderHero = ({ slides }: SliderHeroProps) => {
  const validSlides = useMemo(() => normalizeSlides(slides), [slides])
  const total = validSlides.length
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (total <= 1 || paused) {
      return
    }

    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total)
    }, AUTOPLAY_INTERVAL_MS)

    return () => clearInterval(id)
  }, [paused, total])

  useEffect(() => {
    if (activeIndex >= total) {
      setActiveIndex(0)
    }
  }, [activeIndex, total])

  if (total < 2) {
    return null
  }

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % total)
  }

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev - 1 + total) % total)
  }

  const handleSelect = (index: number) => {
    setActiveIndex(index)
  }

  return (
    <section
      className="relative isolate h-[55vh] w-full min-h-[480px] overflow-hidden bg-black md:h-[85vh]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {validSlides.map((slide, index) => {
        const isActive = index === activeIndex
        const title = slide.title?.trim()
        const secondaryTitle = slide.secondaryTitle?.trim()
        const subtitle = slide.subtitle?.trim()
        const ctaLabel = slide.ctaLabel?.trim()
        const ctaUrl = slide.ctaUrl?.trim()

        return (
          <article
            key={`${slide.imageUrl}-${index}`}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              isActive ? "z-20 opacity-100" : "z-10 opacity-0"
            }`}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${index + 1} of ${total}`}
            aria-hidden={!isActive}
          >
            <Image
              src={slide.imageUrl}
              alt={title || secondaryTitle || "Slider hero banner"}
              fill
              priority={index === 0}
              loading={index === 0 ? "eager" : "lazy"}
              sizes="100vw"
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-black/25 to-transparent" />
            <div className="relative z-10 flex h-full w-full items-center">
              <div className="content-container w-full px-6">
                <div className="flex max-w-4xl flex-col gap-6 px-4 text-left text-white md:gap-8">
                  <div className="space-y-2" aria-live={isActive ? "polite" : undefined}>
                    {title ? (
                      <h2 className="text-xl font-lealti-light leading-tight md:text-6xl">{title}</h2>
                    ) : null}
                    {secondaryTitle ? (
                      <p className="text-lg font-lealti-light text-white/85 md:text-4xl">{secondaryTitle}</p>
                    ) : null}
                  </div>
                  {subtitle ? (
                    <p className="text-base font-lealti-light text-white/80 md:text-lg">{subtitle}</p>
                  ) : null}
                  {ctaLabel && ctaUrl ? (
                    <Link
                      href={ctaUrl}
                      className="inline-flex w-fit items-center gap-3 rounded-full border border-white/70 px-6 py-2 text-sm font-medium text-white transition hover:border-white hover:bg-white/10"
                      prefetch
                    >
                      <span>{ctaLabel}</span>
                      <span aria-hidden>→</span>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          </article>
        )
      })}

      <div className="absolute inset-y-0 left-0 flex items-center px-3">
        <button
          type="button"
          onClick={handlePrevious}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="rounded-full bg-black/40 px-3 py-2 text-white transition hover:bg-black/60"
          aria-label="Previous slide"
        >
          ‹
        </button>
      </div>
      <div className="absolute inset-y-0 right-0 flex items-center px-3">
        <button
          type="button"
          onClick={handleNext}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="rounded-full bg-black/40 px-3 py-2 text-white transition hover:bg-black/60"
          aria-label="Next slide"
        >
          ›
        </button>
      </div>

      <div className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-2">
        {validSlides.map((_, index) => {
          const isActive = index === activeIndex
          return (
            <button
              key={index}
              type="button"
              className={`h-2.5 w-2.5 rounded-full transition ${
                isActive ? "bg-white" : "bg-white/50 hover:bg-white/80"
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-pressed={isActive}
              onClick={() => handleSelect(index)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
            />
          )
        })}
      </div>
    </section>
  )
}

export default SliderHero
